import {
  companyOptions,
  reactivateDisabledUser,
  updateUser,
  useBuilding,
  useCompany,
  useUser,
  userOptions,
  userMetadataOptions,
} from "@/api";
import { Breadcrumbs, Button, Icon, SelectRow } from "@app-components";
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { createContext, useEffect, useState, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Users.module.scss";
import {
  CompanyType,
  NotificationType,
  Timestamp,
  UserAuthMetadata,
} from "@/types";
import { useCompanyAdminAccess } from "@/hooks/useCompanyAdminAccess";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import PageBackButton from "@/components/PageBackButton";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  addMonths,
  endOfQuarter,
  format,
  isAfter,
  isBefore,
  startOfDay,
  startOfQuarter,
} from "date-fns";
import { firebaseTimestampToDate, getBuildingId, notifyApiError } from "@/lib";
import {
  ResignationModal,
  useResignationDate,
} from "@/hooks/useResignationDate";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute(
  "/_portal/company/$companyId/users/$userId",
)({
  component: UserDetails,
  loader: ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    const { companyId } = params;
    if (!queryClient) return;

    queryClient.ensureQueryData(userOptions(params.userId, companyId));
    queryClient.ensureQueryData(userMetadataOptions(params.userId));
    queryClient.ensureQueryData(companyOptions(companyId));
  },
});

export const UserContext = createContext({
  userId: "",
  userDisabled: false,
});

const bannerStyles = {
  marginTop: "var(--wlcm-spacing-sm)",
  marginBottom: "var(--wlcm-spacing-xs)",
};

const bannerInnerStyles = {
  flex: 1,
  display: "flex",
  gap: "var(--wlcm-spacing-sm)",
  minWidth: "60%",
};

const now = new Date();

function UserDetails() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const router = useRouterState();
  const navigate = useNavigate();
  const { userId, companyId } = Route.useParams();
  const { addNotification } = useSnackbarStore();
  const { data: user } = useUser(userId, companyId);
  const { data: userMetadata } = useBuildingQuery({
    ...userMetadataOptions(user.userId),
    enabled: !!user.userId,
  }) as { data?: UserAuthMetadata };
  const { data: company } = useCompany(companyId);
  const { data: building } = useBuilding();
  const [tab, setTab] = useState<string>(router.location.pathname);
  const { hasWelcomeFood, hasMunu } = useWelcomeFoodAccess(true);
  const { allowCompanyAdminAccess } = useCompanyAdminAccess(
    companyId,
    companyId,
  );
  const {
    showResignationModal,
    isSettingResignationDate,
    setResignationDate,
    saveResignationDate,
    setShowResignationModal,
    ...resignationModalProps
  } = useResignationDate(userId, companyId, user.resignationDate);

  // Show warning if user has not been active this quarter and we are within 2 weeks of the end of the quarter
  const showDeletionWarning = useMemo(() => {
    if (!building?.runQuarterlyUserPurge) return false;
    return (
      user.lastActiveDate &&
      isBefore(
        firebaseTimestampToDate(user.lastActiveDate),
        startOfQuarter(now),
      ) &&
      isAfter(now, startOfDay(endOfQuarter(now).setDate(16)))
    );
  }, [user, building]);

  const isExternalCompany = company?.companyType === CompanyType.EXTERNAL;

  const showAccessTab =
    building.accessControlSystemIntegration &&
    company.accessControlSystemIntegration;

  const breadcrumbs = [
    {
      label: t("SIDEBAR.USERS"),
      path: `/company/${company.buildingTenantId}/users`,
    },
    {
      label: `${user?.firstName} ${user?.lastName}`,
      path: `/company/${company.buildingTenantId}/users/${userId}/settings`,
    },
  ];

  const tabs = [
    {
      value: `/company/${company.buildingTenantId}/users/${userId}/settings`,
      label: t("PAGES.USERS.SUB_ROUTES.SETTINGS"),
    },
    ...(!isExternalCompany && showAccessTab
      ? [
          {
            value: `/company/${company.buildingTenantId}/users/${userId}/access`,
            icon: "badge",
            label: "Welcome Access",
          },
        ]
      : []),
    ...(!isExternalCompany &&
    (hasMunu || (hasWelcomeFood && company.foodDeals?.length))
      ? [
          {
            value: `/company/${company.buildingTenantId}/users/${userId}/food`,
            icon: "burger",
            label: "Welcome Food",
          },
        ]
      : []),
  ];

  const { mutate: reactivateUser, isPending: isReactivatingUser } = useMutation(
    {
      mutationFn: async () => {
        if (!userMetadata?.disabled) return;
        return await reactivateDisabledUser(userId, companyId);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "user-metadata", userId],
        });
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "user", userId],
        });
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "users", companyId],
        });
        addNotification(
          t("NOTIFICATIONS.DOCUMENT_UPDATED", {
            entity: t("USER", { count: 1 }).toLowerCase(),
          }),
          NotificationType.SUCCESS,
        );
      },
      onError: (error) => {
        notifyApiError(error, addNotification, t, {
          properties: { entity: t("USER", { count: 1 }).toLowerCase() },
          fallbackKey: "UPDATE_FAILED",
        });
      },
    },
  );

  useEffect(() => {
    if (user.resignationDate) {
      setResignationDate(
        firebaseTimestampToDate(user.resignationDate as unknown as Timestamp),
      );
    }
  }, [user.resignationDate]);

  const { mutate: delayDeletionDate, isPending: isDelaying } = useMutation({
    mutationFn: async () => {
      await updateUser(userId, {
        lastActiveDate: now as unknown as Timestamp,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "user", userId],
      });
      addNotification(
        t("USERS.DELETION_WARNING.DELETION_EXTENDED", {
          newDeletionDate: format(
            endOfQuarter(addMonths(now, 1)),
            "dd.MM.yyyy",
          ),
        }),
        NotificationType.SUCCESS,
      );
    },
    onError: (error) => {
      console.error(error);
      addNotification(t("USERS.DELETION_WARNING.DELETION_EXTENSION_FAILED"));
    },
  });

  if (!allowCompanyAdminAccess) {
    return null;
  }

  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: `/company/${company.buildingTenantId}/users`,
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        {showDeletionWarning && (
          <div
            className="banner dangerBanner"
            style={{ marginBottom: "var(--wlcm-spacing-sm)", flexWrap: "wrap" }}
          >
            <Icon name="auto_delete" />
            <p>
              <Trans
                i18nKey="USERS.DELETION_WARNING.BANNER_CURR_USER"
                values={{
                  lastActiveDate: format(
                    firebaseTimestampToDate(user.lastActiveDate!),
                    "dd.MM.yyyy",
                  ),
                  deletionDate: format(endOfQuarter(new Date()), "dd.MM.yyyy"),
                }}
                components={{
                  bold: <strong></strong>,
                }}
              />
            </p>
            <Button
              secondary
              onClick={delayDeletionDate}
              disabled={isDelaying}
              isLoading={isDelaying}
            >
              {t("USERS.DELETION_WARNING.DELAY_DELETION_BUTTON")}
            </Button>
          </div>
        )}
        {user.resignationDate && userMetadata && !userMetadata.disabled && (
          <div
            className="banner purpleBanner"
            style={{ ...bannerStyles, flexWrap: "wrap" }}
          >
            <div style={bannerInnerStyles}>
              <Icon name="waving_hand" />
              <p>
                <strong>
                  {t("EDIT_USER.RESIGNATION_DATE.BANNER_DESCRIPTION", {
                    date: format(
                      firebaseTimestampToDate(
                        user.resignationDate as unknown as Timestamp,
                      ),
                      "dd.MM.yyyy",
                    ),
                  })}
                </strong>{" "}
                {t("EDIT_USER.RESIGNATION_DATE.DESCRIPTION")}
              </p>
            </div>
            <Button dark onClick={() => setShowResignationModal(true)}>
              {t("EDIT_USER.RESIGNATION_DATE.EDIT")}
            </Button>
            <Button
              secondary
              onClick={() => saveResignationDate(null)}
              disabled={isSettingResignationDate}
              isLoading={isSettingResignationDate}
            >
              {t("EDIT_USER.RESIGNATION_DATE.CANCEL")}
            </Button>
          </div>
        )}
        {userMetadata?.disabled ? (
          <div
            className="banner dark"
            style={{ ...bannerStyles, flexWrap: "wrap" }}
          >
            <div style={bannerInnerStyles}>
              <Icon name="person" />{" "}
              <p>
                <Trans
                  i18nKey="USERS.USER_DISABLED"
                  values={{
                    date:
                      user.resignationDate &&
                      format(
                        firebaseTimestampToDate(
                          user.resignationDate as unknown as Timestamp,
                        ),
                        "dd.MM.yyyy",
                      ),
                  }}
                  components={{
                    hasDate: (
                      <span
                        style={user.resignationDate ? {} : { display: "none" }}
                      ></span>
                    ),
                  }}
                />
              </p>
            </div>
            <Button
              onClick={() => reactivateUser()}
              isLoading={isReactivatingUser}
              disabled={isReactivatingUser}
            >
              {t("USERS.REACTIVATE_USER")}
            </Button>
          </div>
        ) : (
          user.adminInCompanies.includes(user.buildingTenantId) && (
            <p className="banner warningBanner" style={bannerStyles}>
              <Icon name="user_cogwheel" /> {t("USERS.ALSO_ADMIN")}
            </p>
          )
        )}
        {user?.scim?.companyId === user?.buildingTenantId && (
          <p className="banner" style={bannerStyles}>
            <Icon name="sync" /> {t("USERS.PROVISIONED.DESCRIPTION")}
          </p>
        )}
        <h1>
          {user?.firstName} {user?.lastName}{" "}
          <span style={{ opacity: 0.5 }}>({user?.email})</span>
        </h1>
      </div>
      {!isExternalCompany && tabs.length > 1 && (
        <SelectRow
          tabs
          label="Tabs"
          hideLabel
          name="user-details-tabs"
          options={tabs}
          value={tab}
          onChange={(route) => {
            setTab(route as string);
            navigate({
              to: route as string,
            });
          }}
        />
      )}
      <div className={styles.userDetailsSubRouteContainer}>
        <UserContext.Provider
          value={{ userId, userDisabled: !!userMetadata?.disabled }}
        >
          <Outlet />
        </UserContext.Provider>
      </div>
      <ResignationModal
        open={showResignationModal}
        onClose={() => setShowResignationModal(false)}
        setResignationDate={setResignationDate}
        saveResignationDate={saveResignationDate}
        isSettingResignationDate={isSettingResignationDate}
        {...resignationModalProps}
      />
    </>
  );
}
